// Spec 48 ai-ux — staged_change_card_v1.
//
// "Inline card in a thread summarizing an AI-proposed mutation. Header
// states affected count; body shows confidence badge + diff shortcut;
// footer offers Approve / Reject / Edit. Approve/Reject route through
// approval-ux#decision_card_v1." (spec 48 line 770)
//
// Compositional design: this card is the THREAD-INLINE summary; the
// FULL approval surface is decision_card_v1 from spec 47, which the
// caller opens on Review/Approve/Reject. Both routes share semantics —
// this card carries action buttons that fire the same callbacks the
// decision card would.
//
// a11y per spec 48:
//   - Card is role="article" with aria-labelledby pointing to card_title
//   - Action buttons have accessible names that include the affected
//     count — "Approve — 27 employees" not just "Approve"
//   - Status chip uses aria-live="polite" so transitions (approved,
//     applied) are announced

import {
  forwardRef,
  useId,
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
} from "react";

import { cx } from "../lib/cx.ts";

export type StagedChangeStatus =
  | "proposed"
  | "in_review"
  | "approved"
  | "rejected"
  | "expired"
  | "applied";

export type StagedChangeOrigin =
  | { readonly kind: "thread"; readonly label?: string }
  | { readonly kind: "agent_run"; readonly runId: string };

export interface StagedChangeCardProps extends HTMLAttributes<HTMLElement> {
  readonly affectedCount: number;
  /** "27 employees", "all reports-to-Jane", etc. */
  readonly affectedNoun: string;
  readonly changeSummary: string;
  readonly status: StagedChangeStatus;
  readonly origin: StagedChangeOrigin;
  /** Slot for the confidence badge (typically <ConfidenceBadge>). */
  readonly confidenceBadge?: ReactNode;
  /** Decorative card icon. */
  readonly icon?: ReactNode;
  /** Click handler for the diff shortcut button. */
  readonly onViewDiff: (event: MouseEvent<HTMLButtonElement>) => void;
  readonly onApprove: (event: MouseEvent<HTMLButtonElement>) => void;
  readonly onReject: (event: MouseEvent<HTMLButtonElement>) => void;
  readonly onEdit?: (event: MouseEvent<HTMLButtonElement>) => void;
  /** When true, action buttons are disabled (e.g., status !== proposed). */
  readonly actionsDisabled?: boolean;
  // i18n labels:
  readonly viewDiffLabel?: string;
  readonly approveLabel?: string;
  readonly rejectLabel?: string;
  readonly editLabel?: string;
  readonly cardTitleTemplate?: (count: number, noun: string) => string;
  readonly originLabelOverride?: string;
  readonly statusLabelOverride?: string;
}

const STATUS_LABEL: Readonly<Record<StagedChangeStatus, string>> = {
  proposed: "Proposed",
  in_review: "In review",
  approved: "Approved",
  rejected: "Rejected",
  expired: "Expired",
  applied: "Applied",
};

export const StagedChangeCard = forwardRef<HTMLElement, StagedChangeCardProps>(
  (
    {
      affectedCount,
      affectedNoun,
      changeSummary,
      status,
      origin,
      confidenceBadge,
      icon,
      onViewDiff,
      onApprove,
      onReject,
      onEdit,
      actionsDisabled,
      viewDiffLabel = "View diff",
      approveLabel = "Approve",
      rejectLabel = "Reject",
      editLabel = "Edit",
      cardTitleTemplate = (count, noun) => `This changes ${count} ${noun}.`,
      originLabelOverride,
      statusLabelOverride,
      className,
      ...rest
    },
    ref,
  ) => {
    const titleId = useId();
    const subjectSummary = `${affectedCount} ${affectedNoun}`;
    const cardTitle = cardTitleTemplate(affectedCount, affectedNoun);
    const originLabel =
      originLabelOverride ??
      (origin.kind === "thread" ? "From this thread" : `From agent run ${origin.runId}`);
    const statusLabel = statusLabelOverride ?? STATUS_LABEL[status];

    return (
      <article
        ref={ref}
        aria-labelledby={titleId}
        data-component="staged-change-card"
        data-status={status}
        className={cx("nai-staged-change-card", `nai-staged-change-card--${status}`, className)}
        {...rest as HTMLAttributes<HTMLElement>}
      >
        <header className="nai-staged-change-card-header">
          {icon !== undefined && (
            <span
              aria-hidden="true"
              data-component="staged-change-card-icon"
              className="nai-staged-change-card-icon"
            >
              {icon}
            </span>
          )}
          <h3
            id={titleId}
            data-component="staged-change-card-title"
            className="nai-staged-change-card-title"
          >
            {cardTitle}
          </h3>
          <span
            aria-live="polite"
            data-component="staged-change-card-status-chip"
            className="nai-staged-change-card-status-chip"
          >
            {statusLabel}
          </span>
        </header>
        <p
          data-component="staged-change-card-summary"
          className="nai-staged-change-card-summary"
        >
          {changeSummary}
        </p>
        <div className="nai-staged-change-card-meta">
          {confidenceBadge}
          <span
            data-component="staged-change-card-origin-chip"
            className="nai-staged-change-card-origin-chip"
          >
            {originLabel}
          </span>
        </div>
        <div className="nai-staged-change-card-actions" role="group" aria-label="Staged change actions">
          <button
            type="button"
            data-component="staged-change-card-view-diff"
            className="nai-staged-change-card-view-diff"
            onClick={onViewDiff}
          >
            {viewDiffLabel}
          </button>
          <button
            type="button"
            data-component="staged-change-card-approve"
            className="nai-staged-change-card-approve"
            disabled={actionsDisabled === true}
            aria-label={`${approveLabel} — ${subjectSummary}`}
            onClick={onApprove}
          >
            {approveLabel}
          </button>
          <button
            type="button"
            data-component="staged-change-card-reject"
            className="nai-staged-change-card-reject"
            disabled={actionsDisabled === true}
            aria-label={`${rejectLabel} — ${subjectSummary}`}
            onClick={onReject}
          >
            {rejectLabel}
          </button>
          {onEdit !== undefined && (
            <button
              type="button"
              data-component="staged-change-card-edit"
              className="nai-staged-change-card-edit"
              disabled={actionsDisabled === true}
              aria-label={`${editLabel} — ${subjectSummary}`}
              onClick={onEdit}
            >
              {editLabel}
            </button>
          )}
        </div>
      </article>
    );
  },
);
StagedChangeCard.displayName = "StagedChangeCard";
